// src/ui/screens/char-select/descriptors.ts — P5 D-27 named-descriptor buckets.
//
// Thresholds live here (separate from the consumer) so P7 tuning can adjust
// the bucket boundaries without touching the CharacterCard / CharacterDetailPane
// views. Mirrors the src/sim/ai/archetypes/constants.ts convention: tuning
// constants in a dedicated module.
//
// The 5 stat rows rendered on the character-select detail pane are, in order:
//   Hiding  → hideSkill                    (direct 0..1)
//   Sight   → see.stat                     (direct 0..1)
//   Hearing → hear.stat                    (direct 0..1)
//   Speed   → baseSpeedMultiplier × sprint (derived product, ~0.6..2.4)
//   Nerve   → fear.overall                 (INVERTED — low fear = high nerve)
//
// Each stat has two exports:
//   xxxDescriptor(v) → human-readable string ("Excellent", "Sharp", ...)
//   xxxTier(v)       → integer 0..3 for the 4-segment bar visualization
//                      (3 = highest bucket, 0 = lowest)
//
// Source: .planning/phases/05-content-pipeline-characters-admin-cms/05-UI-SPEC.md
//         §Named-Descriptor Stat Buckets.

export type DescriptorTier = 0 | 1 | 2 | 3;

// --- Hiding (hideSkill) ---------------------------------------------------
// ≥0.8 Excellent / 0.6..0.79 Sneaky / 0.4..0.59 Average / <0.4 Clumsy
export function hideSkillDescriptor(v: number): string {
  if (v >= 0.8) return 'Excellent';
  if (v >= 0.6) return 'Sneaky';
  if (v >= 0.4) return 'Average';
  return 'Clumsy';
}
export function hideSkillTier(v: number): DescriptorTier {
  if (v >= 0.8) return 3;
  if (v >= 0.6) return 2;
  if (v >= 0.4) return 1;
  return 0;
}

// --- Nerve (fear.overall, inverted) --------------------------------------
// <0.25 Fearless / 0.25..0.49 Brave / 0.5..0.65 Wary / >0.65 Jumpy
// NOTE: Wary is inclusive-inclusive 0.5..0.65 per UI-SPEC; Jumpy is strict >0.65.
// Low fear ⇒ high nerve, so tier is inverted: Fearless=3, Brave=2, Wary=1, Jumpy=0.
export function fearDescriptor(overall: number): string {
  if (overall < 0.25) return 'Fearless';
  if (overall < 0.5) return 'Brave';
  if (overall <= 0.65) return 'Wary';
  return 'Jumpy';
}
export function fearTier(overall: number): DescriptorTier {
  if (overall < 0.25) return 3;
  if (overall < 0.5) return 2;
  if (overall <= 0.65) return 1;
  return 0;
}

// --- Sight (see.stat) -----------------------------------------------------
// ≥0.65 Sharp / 0.5..0.64 Good / 0.35..0.49 Okay / <0.35 Nearsighted
export function sightDescriptor(stat: number): string {
  if (stat >= 0.65) return 'Sharp';
  if (stat >= 0.5) return 'Good';
  if (stat >= 0.35) return 'Okay';
  return 'Nearsighted';
}
export function sightTier(stat: number): DescriptorTier {
  if (stat >= 0.65) return 3;
  if (stat >= 0.5) return 2;
  if (stat >= 0.35) return 1;
  return 0;
}

// --- Hearing (hear.stat) --------------------------------------------------
// ≥0.65 Sharp / 0.5..0.64 Good / 0.35..0.49 Okay / <0.35 Muffled
export function hearingDescriptor(stat: number): string {
  if (stat >= 0.65) return 'Sharp';
  if (stat >= 0.5) return 'Good';
  if (stat >= 0.35) return 'Okay';
  return 'Muffled';
}
export function hearingTier(stat: number): DescriptorTier {
  if (stat >= 0.65) return 3;
  if (stat >= 0.5) return 2;
  if (stat >= 0.35) return 1;
  return 0;
}

// --- Speed (baseSpeedMultiplier × sprintMultiplier) ----------------------
// ≥2.0 Fast / 1.5..1.99 Quick / 1.0..1.49 Steady / <1.0 Slow
export function speedDescriptor(base: number, sprint: number): string {
  const top = base * sprint;
  if (top >= 2.0) return 'Fast';
  if (top >= 1.5) return 'Quick';
  if (top >= 1.0) return 'Steady';
  return 'Slow';
}
export function speedTier(base: number, sprint: number): DescriptorTier {
  const top = base * sprint;
  if (top >= 2.0) return 3;
  if (top >= 1.5) return 2;
  if (top >= 1.0) return 1;
  return 0;
}
